import React, { useRef, useEffect, useState } from 'react';
import { createClient } from '@sanity/client';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const Retreats2024 = () => {
  const [gljsMap, setGLJSMap] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [markers, setMarkers] = useState([]);
  const mapContainerRef = useRef(null);
  const sanity = createClient({
    projectId: 'xcve8qei',
    dataset: 'production',
    apiVersion: '2023-05-03',
    useCdn: false,
  });

  useEffect(() => {
    (async () => {
      const mobile = navigator?.userAgentData?.mobile || (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substring(0,4))) === true;
      setIsMobile(mobile);
      const pointsData = (await sanity.fetch('*[_type == "retreats2024entry"]{ _id, location, type, title, timestamp, teaser, autoPopup9, autoPopup10, autoPopup11, autoPopup12, author, link, "bannerUrl": banner.asset->url }')).map((entry, i) => {
        return {
          id: entry._id,
          coordinates: [entry.location.lng, entry.location.lat],
          type: entry.type,
          title: entry.title,
          teaser: entry.teaser,
          author: entry.author,
          bannerUrl: entry.bannerUrl,
          timestamp: entry.timestamp,
          autoPopup9: entry.autoPopup9,
          autoPopup10: entry.autoPopup10,
          autoPopup11: entry.autoPopup11,
          autoPopup12: entry.autoPopup12,
          link: entry.link,
        }
      });

      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/tlampert-lsoc/cltjkzn3800m801qpagbtagra',
        accessToken: 'pk.eyJ1IjoidGxhbXBlcnQtbHNvYyIsImEiOiJjbHRpZXRtZDAwZWN6MmptdWNuYzV2eDVmIn0.u5UOL3mm3xPTpck8LMHTdw',
        center: [-88.04989323314561, 41.889771460301944],
        zoom: mobile ? 7.521791487658497 : 8.521791487658497
      });

      map.addControl(new mapboxgl.NavigationControl());
      if(!mobile) map.scrollZoom.disable();

      map.on('load', () => {
        setGLJSMap(map);
        const refresh = () => {
          console.log(map.getZoom(), map.getCenter());
          setTimeout(refresh, 100);
        }
        refresh();
        map.resize();

        const tempMarkers = [];
        document.querySelectorAll('.mapboxgl-popup').forEach((popup) => popup.remove());

        pointsData.forEach((point) => {
          const marker = new mapboxgl.Marker({ color: point.type === 'story' ? '#dd8e18' : '#183058' })
            .setLngLat(point.coordinates)
            .addTo(map);
          tempMarkers.push({ point, marker });
          const popup = new mapboxgl.Popup({ closeOnClick: true })
            .setHTML(point.type === 'story' ? `
              <div class="card">
                <img src="${point.bannerUrl}" style="width: 100%;">
                <div class="card-content card-content-story">
                  <h1><a href="${point.link}" target="_parent">${point.title}</a></h1>
                  <p class="mulish-regular">${point.teaser}</p>
                  <a class="read-button-link" href="${point.link}" target="_parent">Read Story</a>
                  <p class="mulish-regular">${new Date(point.timestamp).toLocaleDateString()} | ${point.author}</p>
                </div>
              </div>
              ` : `
              <div class="card">
                <img src="${point.bannerUrl}" style="width: 100%;">
                <div class="card-content card-content-ig">
                  <p class="mulish-regular">${point.teaser}</p>
                  <a class="ig-button-link mulish-regular" href="${point.link}" target="_parent">Instagram</a>
                  <p class="mulish-regular">${new Date(point.timestamp).toLocaleDateString()} | ${point.author}</p>
                </div>
              </div>
            `);

          marker.setPopup(popup);
          // if (point.autoPopup9) marker.togglePopup();

          // if (!point.autoPopup9) {
          //   marker.on('mouseenter', () => {
          //     marker.getPopup().addTo(map);
          //   });

          //   marker.on('mouseleave', () => {
          //     marker.getPopup().remove();
          //   });
          // }
        });

        setMarkers(tempMarkers);
      });
    })();
  }, []);

  const zoom = (to) => {
    if (to === 'all') {
      markers.forEach(({ point, marker }) => {
        if(marker.getPopup().isOpen()) marker.togglePopup();
      });

      gljsMap.flyTo({
        center: [-88.04989323314561, 41.889771460301944],
        zoom: isMobile ? 7.521791487658497 : 8.521791487658497,
      });
    } else if (to === '9th') {
      markers.forEach(({ point, marker }) => {
        if(marker.getPopup().isOpen() && !point.autoPopup9) marker.togglePopup();
        if(!marker.getPopup().isOpen() && point.autoPopup9) marker.togglePopup();
      });

      gljsMap.flyTo({
        center: isMobile ? [-88.08841180602403, 41.69364362340997] : [-87.83017966159885, 41.71225536133622],
        zoom: 9.595080273793553
      });
    } else if (to === '10th') {
      markers.forEach(({ point, marker }) => {
        if(marker.getPopup().isOpen() && !point.autoPopup10) marker.togglePopup();
        if(!marker.getPopup().isOpen() && point.autoPopup10) marker.togglePopup();
      });

      gljsMap.flyTo({
        center: isMobile ? [-87.70400808576372, 42.075332898067415] : [-87.77664134513968, 42.07187438752828],
        zoom: 10.595080273793554
      });
    } else if (to === '11th') {
      markers.forEach(({ point, marker }) => {
        if(marker.getPopup().isOpen() && !point.autoPopup11) marker.togglePopup();
        if(!marker.getPopup().isOpen() && point.autoPopup11) marker.togglePopup();
      });

      gljsMap.flyTo({
        center: isMobile ? [-87.72424283630846, 41.97820305425128] : [-87.85202985087153, 41.989604430790564],
        zoom: 10.595080273793554
      });
    } else if (to === '12th') {
      markers.forEach(({ point, marker }) => {
        if(marker.getPopup().isOpen() && !point.autoPopup12) marker.togglePopup();
        if(!marker.getPopup().isOpen() && point.autoPopup12) marker.togglePopup();
      });

      gljsMap.flyTo({
        center: isMobile ? [-88.23528210716245, 42.10856932085673] : [-88.03785324909028, 42.091949375066235],
        zoom: 9.396092502887312
      });
    }
  }

  return (
    <>
      <div className="mulish-regular" style={{ textAlign: 'center' }}>
        <p>
          <strong>Focus:</strong> | <a href="#" onClick={() => zoom('all')}>All</a> | <a href="#" onClick={() => zoom('9th')}>9th</a> | <a href="#" onClick={() => zoom('10th')}>10th</a> | <a href="#" onClick={() => zoom('11th')}>11th</a> | <a href="#" onClick={() => zoom('12th')}>12th</a>
        </p>
      </div>
      <div id="mapbox" ref={mapContainerRef} />
    </>
  );
};

export default Retreats2024;